import { addInterceptor } from "@/_error";
import * as Sentry from "@sentry/browser";

const sentryDSNTag = document.querySelector('meta[name="sentry-dsn"]')?.getAttribute("content");
const revision = document.querySelector('meta[name="tm-release"]')?.getAttribute("content") || "none";
const stage = document.querySelector('meta[name="tm-env"]')?.getAttribute("content") || "unknown";

import type { Analytics } from "../cookies/v4_analytics";

// Send User Feedback
interface FeedbackIntegration {
    integration: any;
    widget: any;
}

const SENTRY_TRANSLATION_KEYS = [
    "triggerLabel",
    "formTitle",
    "submitButtonLabel",
    "cancelButtonLabel",
    "confirmButtonLabel",
    "addScreenshotButtonLabel",
    "removeScreenshotButtonLabel",
    "nameLabel",
    "namePlaceholder",
    "emailLabel",
    "emailPlaceholder",
    "isRequiredLabel",
    "messageLabel",
    "messagePlaceholder",
    "successMessageText",
]


export default class SentryAnalytics implements Analytics {
    private running: boolean = false;
    private initialized: boolean = false;

    private feedback: FeedbackIntegration|null = null;
    /**
     * initialize the analytics call chain.
     */
    async initialize() {
        this.running = true;
        if (this.initialized) return;
        this.initialized = true;

        Sentry.init({
            dsn: sentryDSNTag!,
            release: revision,
            environment: stage,
            autoSessionTracking: false,
            defaultIntegrations: false,
            tunnel: "/sentry",
            integrations(integrations) {
                const SKIP = [
                    "Breadcrumbs",
                    "CaptureConsole",
                    "GlobalHandlers",
                    "BrowserApiErrors"
                ];
                const defaultIntegrations = integrations.filter((integration) => {
                    return !SKIP.includes(integration.name);
                    
                });
                return [...defaultIntegrations];
            }
        });

        // Send errors to Sentry.
        addInterceptor(900, (value: any, extras: Record<string, any>) => {
            if (!this.running) return false;
            if (extras["telemetry"] === false) return false;
            Sentry.withScope((scope) => {
                for (let [k, v] of Object.entries(extras)) {
                    scope.setExtra(k, v);
                }

                if (value instanceof Error) {
                    Sentry.captureException(value);
                } else {
                    Sentry.captureMessage("" + value);
                }
            });
            return false;
        });

        addInterceptor(900, (_: any, extras: Record<string, any>) => {
            if (!this.running) return false;
            if (extras["telemetry"] === false) return false;

            const tryCapture = (e: any) => {
                try {
                    Sentry.captureException(e)
                } catch (e2) {
                    console.error(e2);
                }
            }
            (async () => {
                const { tr } = await import("@/utils/translations");
                if (this.feedback) return;

                let currentKeys: Record<string, string> = {};
                try {
                    currentKeys = Object.fromEntries(SENTRY_TRANSLATION_KEYS.map(k => [k, tr(`sentry.feedback.${k}`)]));
                } catch (e) {
                    tryCapture(e);
                }

                const feedback = Sentry.feedbackIntegration({
                    ...currentKeys,
                    onSubmitSuccess() {
                        this.feedback?.widget.removeFromDom();
                        this.feedback = null;
                    },
                    onSubmitError() {
                        this.feedback?.widget.removeFromDom();
                        this.feedback = null;
                    }
                });

                const widget = feedback.createWidget({});
                this.feedback = {
                    integration: feedback,
                    widget: widget
                }
            })().catch(tryCapture);
            return false;
        });
    }

    async event() {}

    async reload() {}

    async close() {
        window.location.reload()
    }
}


